import { useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import classNames from "classnames"
import styled from "styled-components"

import {
  useAddWHManagerOrderTrackingNumbersMutation,
  useShipWHManagerOrderMutation,
} from "store/api/warehouseManagerApi"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconPlus } from "assets/svg/icon-plus.svg"
import { ReactComponent as IconShipping } from "assets/svg/modal-icon-shipping.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { ButtonNew } from "components/form/ButtonNew"
import { InputNew } from "components/form/InputNew"

const TrackingNumbersWrapper = styled.div`
  margin-top: 24px;
  text-align: left;
  color: #64748b;
`

const AddMoreButton = styled(ButtonNew)`
  color: #64748b !important;
  font-weight: 400;
`

const StyledIconPlus = styled(IconPlus)`
  width: 20px;
  height: 20px;
`

interface Props {
  onCancel: () => void
  onSuccess: () => void
  customOnSubmit?: (event: React.FormEvent) => Promise<boolean>
  isFetching?: boolean
  orderItemId: number
}

export const ShippingModal = ({
  onCancel,
  onSuccess,
  customOnSubmit,
  isFetching: customIsFetching,
  orderItemId,
}: Props) => {
  const [triggerShipOrder, { isLoading: defaultIsFetching }] = useShipWHManagerOrderMutation()
  const [addTrackingNumbers, { isLoading: isAddingTracking }] = useAddWHManagerOrderTrackingNumbersMutation()
  const isFetching = (customIsFetching ?? defaultIsFetching) || isAddingTracking

  const [trackingNumbers, setTrackingNumbers] = useState<string[]>([""])
  const [trackingNumberErrors, setTrackingNumberErrors] = useState<string[]>([""])

  const hasValidationErrors = trackingNumberErrors.some(error => error !== "")

  const handleTrackingNumberChange = (index: number, value: string) => {
    const trimmedValue = value.trim()
    const hasSpacesBetweenWords = /\s/.test(trimmedValue)
    const errorMessage = hasSpacesBetweenWords ? "Tracking number cannot contain spaces" : ""

    setTrackingNumbers(trackingNumbers.map((tn, i) => (i === index ? value : tn)))
    setTrackingNumberErrors(trackingNumberErrors.map((err, i) => (i === index ? errorMessage : err)))
  }

  const handleAddMore = () => {
    setTrackingNumbers([...trackingNumbers, ""])
    setTrackingNumberErrors([...trackingNumberErrors, ""])
  }

  const handleAddTrackingNumbers = async () => {
    if (!orderItemId) return

    const validTrackingNumbers = trackingNumbers.filter(tn => tn.trim() !== "").map(tn => tn.trim())

    if (validTrackingNumbers.length === 0) return

    try {
      await addTrackingNumbers({
        id: orderItemId,
        trackingNumbers: validTrackingNumbers,
      }).unwrap()
    } catch (error) {
      const errorMessage = getErrorMessage(error as FetchBaseQueryError | SerializedError | undefined)
      if (errorMessage) {
        toast.error(errorMessage)
      }
    }
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    let success = false

    if (customOnSubmit) {
      success = await customOnSubmit(event)
    } else {
      if (!orderItemId) return

      try {
        const res = await triggerShipOrder(orderItemId)
        const error = getErrorMessage(res?.error)

        if (error) {
          toast.error(error)
          return
        } else {
          toast.success("Order shipped successfully!")
          success = true
        }
      } catch (error) {
        console.error(error)
        return
      }
    }

    if (success && orderItemId) {
      await handleAddTrackingNumbers()
    }

    if (success) {
      onSuccess()
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="560px"
          isFetching={isFetching}
          isDisabled={hasValidationErrors}
          onConfirm={onSubmit}
          onCancel={onCancel}
          confirmButtonText="Yes"
        >
          <div className={classNames(styles.modalContent, styles.center)}>
            <IconShipping />
            <h3 className={styles.modalTitle}>Shipping</h3>
            <div className={styles.modalMessage}>Are you sure you want to ship the Order number {orderItemId}?</div>
            <TrackingNumbersWrapper>
              {trackingNumbers.map((trackingNumber, index) => (
                <InputNew
                  key={index}
                  label={index === 0 ? "Tracking number" : undefined}
                  placeholder="Input tracking number"
                  value={trackingNumber}
                  onChange={e => handleTrackingNumberChange(index, e.currentTarget.value)}
                  errorMessage={trackingNumberErrors[index]}
                />
              ))}
              <AddMoreButton color="greyBordered" size="auto" onClick={handleAddMore}>
                <StyledIconPlus /> Add more
              </AddMoreButton>
            </TrackingNumbersWrapper>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
